import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldX, RefreshCw, Mail, AlertTriangle, Ban, Clock, ArrowRight, Lock } from "lucide-react";
import { auth, userSettings } from "../../services/api/client";
import { useI18n } from "../../i18n";

// ─── Status configurations (factory — requires t for translations) ──────────
const getStatusConfigs = (t) => ({
  expired: {
    icon: Clock,
    iconColor: "text-yellow-400",
    borderColor: "border-yellow-400/30",
    bgGlow: "from-yellow-900/20",
    badge: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20",
    badgeText: t("license.expiredBadge"),
    title: t("license.expiredTitle"),
    subtitle: t("license.expiredSubtitle"),
    actions: [
      { label: t("license.renewAction"), href: "mailto:contact@sylink.fr?subject=Renouvellement licence SYLink", primary: true },
      { label: t("license.accessSettings"), href: "/account?tab=settings", primary: false },
    ],
  },
  revoked: {
    icon: Ban,
    iconColor: "text-red-400",
    borderColor: "border-red-400/30",
    bgGlow: "from-red-900/20",
    badge: "bg-red-500/10 text-red-400 border border-red-500/20",
    badgeText: t("license.revokedBadge"),
    title: t("license.revokedTitle"),
    subtitle: t("license.revokedSubtitle"),
    actions: [
      { label: t("license.contactSyLink"), href: "mailto:contact@sylink.fr?subject=SYLink License Revocation", primary: true },
    ],
  },
  cancelled: {
    icon: ShieldX,
    iconColor: "text-orange-400",
    borderColor: "border-orange-400/30",
    bgGlow: "from-orange-900/20",
    badge: "bg-orange-500/10 text-orange-400 border border-orange-500/20",
    badgeText: t("license.cancelledBadge"),
    title: t("license.cancelledTitle"),
    subtitle: t("license.cancelledSubtitle"),
    actions: [
      { label: t("license.subscribeAction"), href: "mailto:contact@sylink.fr?subject=Nouveau abonnement SYLink", primary: true },
      { label: t("license.exportData"), href: "/account?tab=reports", primary: false },
    ],
  },
  no_license: {
    icon: Lock,
    iconColor: "text-gray-400",
    borderColor: "border-gray-400/30",
    bgGlow: "from-gray-900/20",
    badge: "bg-gray-500/10 text-gray-400 border border-gray-500/20",
    badgeText: t("license.noLicenseBadge"),
    title: t("license.noLicenseTitle"),
    subtitle: t("license.noLicenseSubtitle"),
    actions: [
      { label: t("license.activateLicense"), href: "/account?tab=settings", primary: true },
      { label: t("license.contactSyLink"), href: "mailto:contact@sylink.fr?subject=Licence SYLink", primary: false },
    ],
  },
});

// ─── Animated grid background ─────────────────────────────────────────────
const GridBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div
      className="absolute inset-0 opacity-10"
      style={{
        backgroundImage: `
          linear-gradient(to right, #00ff88 1px, transparent 1px),
          linear-gradient(to bottom, #00ff88 1px, transparent 1px)
        `,
        backgroundSize: "40px 40px",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-black/50 to-brand-black" />
  </div>
);

// ─── Main page ─────────────────────────────────────────────────────────────
export default function LicenseExpiredPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Determine status from query param or auto-detect
  const statusParam = searchParams.get("reason") || "expired";
  const STATUS_CONFIGS = getStatusConfigs(t);
  const config = STATUS_CONFIGS[statusParam] || STATUS_CONFIGS.expired;
  const IconComponent = config.icon;

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const [userData, settingsData] = await Promise.allSettled([
          auth.getCurrentUser(),
          userSettings.get(),
        ]);

        if (userData.status === "fulfilled") {
          const u = userData.value;
          setUserEmail(u.email || "");
          setIsSuperAdmin(u.role === "super_admin" || u.is_superuser);
        }

        if (settingsData.status === "fulfilled") {
          const lic = settingsData.value?.license_settings || {};
          setLicenseInfo(lic);
        }
      } catch (_) {
        // silently ignore
      } finally {
        setLoading(false);
      }
    };
    loadInfo();
  }, []);

  // Superadmin bypass — redirect back to dashboard
  useEffect(() => {
    if (!loading && isSuperAdmin) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, isSuperAdmin, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-black">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black flex items-center justify-center p-4 relative overflow-hidden">
      <GridBackground />

      {/* Main card */}
      <div className="relative z-10 max-w-2xl w-full">

        {/* Glow effect behind card */}
        <div className={`absolute -inset-px rounded-2xl bg-gradient-to-b ${config.bgGlow} to-transparent blur-xl`} />

        <div className={`relative bg-gray-900/80 backdrop-blur-xl rounded-2xl border ${config.borderColor} p-8 shadow-2xl`}>

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="SYLink" className="h-8 opacity-60" />
          </div>

          {/* Status badge */}
          <div className="flex justify-center mb-6">
            <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-widest ${config.badge}`}>
              {config.badgeText}
            </span>
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`w-20 h-20 rounded-full bg-gray-800/60 border ${config.borderColor} flex items-center justify-center`}>
              <IconComponent className={`w-10 h-10 ${config.iconColor}`} />
            </div>
          </div>

          {/* Title & subtitle */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-3">{config.title}</h1>
            <p className="text-gray-400 text-sm leading-relaxed max-w-md mx-auto">
              {config.subtitle}
            </p>
          </div>

          {/* License info block */}
          {licenseInfo && (licenseInfo.license_id || licenseInfo.expires_at) && (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-4 mb-6 space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">{t("license.licenseInfo")}</p>
              {licenseInfo.license_id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t("license.reference")}</span>
                  <span className="text-white font-mono">{licenseInfo.license_id}</span>
                </div>
              )}
              {licenseInfo.tier && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t("license.tier")}</span>
                  <span className="text-white">{licenseInfo.tier}</span>
                </div>
              )}
              {licenseInfo.expires_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t("license.expiredOn")}</span>
                  <span className={`font-medium ${statusParam === "expired" ? "text-yellow-400" : "text-gray-300"}`}>
                    {formatDate(licenseInfo.expires_at)}
                  </span>
                </div>
              )}
              {licenseInfo.customer?.account_company && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t("license.holder")}</span>
                  <span className="text-white">{licenseInfo.customer.account_company}</span>
                </div>
              )}
            </div>
          )}

          {/* Warning message */}
          <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 mb-8 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300 leading-relaxed">
              {statusParam === "expired" && (
                <>
                  {t("license.warningExpired")}
                  {licenseInfo?.customer?.account_email && (
                    <span> {t("license.warningExpiredEmail")} <strong className="text-white">{licenseInfo.customer.account_email}</strong>.</span>
                  )}
                </>
              )}
              {statusParam === "revoked" && t("license.warningRevoked")}
              {statusParam === "cancelled" && t("license.warningCancelled")}
              {statusParam === "no_license" && t("license.warningNoLicense")}
            </div>
          </div>

          {/* CTA buttons */}
          <div className="space-y-3">
            {config.actions.map((action, idx) => (
              action.href.startsWith("mailto") ? (
                <a
                  key={idx}
                  href={action.href}
                  className={`flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                    action.primary
                      ? "bg-brand-green text-black hover:bg-brand-green/90 hover:shadow-lg hover:shadow-brand-green/20"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-700"
                  }`}
                >
                  {action.primary ? <RefreshCw className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                  {action.label}
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <button
                  key={idx}
                  onClick={() => navigate(action.href)}
                  className={`flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                    action.primary
                      ? "bg-brand-green text-black hover:bg-brand-green/90 hover:shadow-lg hover:shadow-brand-green/20"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-700"
                  }`}
                >
                  {action.primary ? <RefreshCw className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                  {action.label}
                </button>
              )
            ))}
          </div>

          {/* Contact footer */}
          <div className="mt-8 pt-6 border-t border-gray-800 text-center">
            <p className="text-xs text-gray-500">
              {t("license.needHelp")}{" "}
              <a
                href="mailto:contact@sylink.fr"
                className="text-brand-green hover:underline"
              >
                contact@sylink.fr
              </a>{" "}
              — SYLink Security, 7j/7
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
